import { http, isApiError } from '@noor/api';
import { env } from '@noor/config';
import type * as z from 'zod';
import type { VersionHistoryPage } from '../model/schema';

/** Search-service admin routes live on the Delivery host, not the main gateway. */
const deliveryHost = { baseURL: env.VITE_DELIVERY_API_URL };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Typed client for one versioned search-service resource.
 *
 * - `list` — `GET {path}?limit&skip` → `{ data: { total_count }, list, meta }`.
 * - `active` — `GET {path}/active` → `{ data: { version } }`; a resource that
 *   was never configured answers 404 (or an empty body), read as "no version".
 * - `create` — `POST {path}` → `{ data: { version } }`.
 *
 * Responses are parsed at the boundary so contract drift fails loudly.
 */
export function createVersionedSettingsApi<
  TSchema extends z.ZodType,
  TInput extends object,
>(path: string, versionSchema: TSchema) {
  type TVersion = z.output<TSchema>;

  function readVersion(raw: unknown): TVersion | null {
    const data = isRecord(raw) ? raw.data : undefined;
    const version = isRecord(data) ? data.version : undefined;
    if (!isRecord(version)) return null;
    return versionSchema.parse(version);
  }

  return {
    list: async (params: {
      limit: number;
      skip: number;
    }): Promise<VersionHistoryPage<TVersion>> => {
      const raw = await http.get<unknown>(path, { ...deliveryHost, query: params });
      const root = isRecord(raw) ? raw : {};
      const rows = Array.isArray(root.list) ? root.list : [];
      const list = rows.map((row) => versionSchema.parse(row));
      const meta = isRecord(root.meta) ? root.meta : {};
      const data = isRecord(root.data) ? root.data : {};
      const total = Number(meta.total ?? data.total_count ?? list.length);
      const pageCount = Number(meta.page_count ?? Math.ceil(total / params.limit));
      return { list, total, pageCount: Math.max(1, pageCount) };
    },

    active: async (): Promise<TVersion | null> => {
      try {
        return readVersion(await http.get<unknown>(`${path}/active`, deliveryHost));
      } catch (error) {
        if (isApiError(error) && error.isNotFound) return null;
        throw error;
      }
    },

    create: async (input: TInput): Promise<TVersion> => {
      const version = readVersion(await http.post<unknown>(path, input, deliveryHost));
      if (!version) throw new Error(`Empty response from POST ${path}`);
      return version;
    },
  };
}
